/* Firebase Auth, loaded from gstatic at runtime rather than bundled.

   The SDK is only needed on the sign-in and sign-out paths, so a signed-in visit
   never fetches it and `dist/` never carries it. It is the app's only
   cross-origin runtime dependency.

   Lives in core/ because it touches no DOM and no Lit: g-signin owns the UI,
   this owns the SDK. Everything Firebase-shaped stops here — callers deal in an
   ID token and a boolean, never in a provider or a credential.

   The version is pinned. Bump it deliberately. */

import { getAuthConfig } from './api.js';

const SDK_VERSION = '11.6.0';

/** The SDK surface we actually use. `object` stands in for the SDK's opaque
    handles — we only ever pass them straight back to it. */
interface AppModule {
  initializeApp(config: Record<string, string>): object;
}
interface AuthModule {
  getAuth(app: object): object;
  GoogleAuthProvider: new () => object;
  signInWithPopup(auth: object, provider: object): Promise<{
    user: { getIdToken(): Promise<string> };
  }>;
  signOut(auth: object): Promise<void>;
}

let loaded: Promise<{ auth: object; mod: AuthModule }> | undefined;

/**
 * Load + initialise the SDK once per page.
 *
 * Memoised on the promise, not the result, so two overlapping calls (sign-in
 * clicked twice) share one download rather than initialising two apps.
 */
function load(): Promise<{ auth: object; mod: AuthModule }> {
  loaded ??= (async () => {
    const cfg = await getAuthConfig();
    const base = `https://www.gstatic.com/firebasejs/${SDK_VERSION}`;
    // Template literals, not string constants: a statically analysable specifier
    // would make the bundler try to resolve gstatic at build time.
    const [appMod, authMod] = (await Promise.all([
      import(/* @vite-ignore */ `${base}/firebase-app.js`),
      import(/* @vite-ignore */ `${base}/firebase-auth.js`),
    ])) as [AppModule, AuthModule];
    const app = appMod.initializeApp({
      apiKey: cfg.apiKey,
      authDomain: cfg.authDomain,
      projectId: cfg.projectId,
      appId: cfg.appId,
    });
    return { auth: authMod.getAuth(app), mod: authMod };
  })();
  return loaded;
}

/** Firebase's codes for "the user backed out", which is not a failure. */
const CANCELLED = new Set(['auth/popup-closed-by-user', 'auth/cancelled-popup-request']);

/**
 * Google sign-in popup. Resolves to a Firebase ID token, or **null** when the
 * user closed the popup — that is a cancellation, not an error, and the caller
 * should say nothing rather than show a failure.
 *
 * Anything else throws.
 */
export async function signInWithGoogle(): Promise<string | null> {
  const { auth, mod } = await load();
  try {
    const cred = await mod.signInWithPopup(auth, new mod.GoogleAuthProvider());
    return await cred.user.getIdToken();
  } catch (e) {
    if (CANCELLED.has((e as { code?: string })?.code ?? '')) return null;
    throw e;
  }
}

/**
 * Best-effort Firebase sign-out. Never throws.
 *
 * Cosmetic on its own — our session cookie is what actually gates access — but
 * skipping it makes "Sign out" look broken: the next popup silently re-selects
 * the account you just signed out of and lands you straight back in.
 */
export async function signOutOfFirebase(): Promise<void> {
  try {
    const { auth, mod } = await load();
    await mod.signOut(auth);
  } catch {
    /* nothing to sign out of, or the SDK never loaded */
  }
}
